import React, { useEffect, useRef, useState } from "react";
import CustomButton from "../shared/CustomButton";
import { AddPlus, RemoveMinus, Trash } from "../normalizer";

// Delay (ms) before a quantity change is actually sent to the cart API, so
// typing a multi-digit number or clicking +/- repeatedly results in a single
// request (and a single toast) instead of one per keystroke/click.
const QUANTITY_DEBOUNCE_MS = 600;

// Editable quantity control for a cart line item.
// A numeric input flanked by a remove (trash), minus and plus control. The
// browser spin-buttons are suppressed via CSS (.cart-quantity-input__field) so
// the only way to change the value is the buttons or direct typing. The field
// is locally controlled while the user edits and the canonical item.quantity
// takes over again once the API responds.
const CartItemsQuantityInput = (props) => {
  const {
    item,
    onIncreaseItem,
    onDecreaseItem,
    onSetItemQuantity,
    onRemoveItem,
  } = props;
  const quantity = item.quantity;
  const options =
    item && item.wallet && item.wallet.campaign_options
      ? item.wallet.campaign_options.options
      : {
          minimum_quantity: 1,
          maximum_quantity: null,
        };

  const minimum = options.minimum_quantity || 1;
  // A null/undefined `maximum_quantity` means there is no upper limit, so the
  // plus control stays available.
  const maximum =
    options.maximum_quantity === null || options.maximum_quantity === undefined
      ? null
      : options.maximum_quantity;

  const [value, setValue] = useState(quantity);
  const debounceRef = useRef(null);

  // The server is the source of truth: realign the field whenever the cart
  // comes back with a new quantity (or the debounced change was rejected).
  useEffect(() => {
    setValue(quantity);
  }, [quantity]);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  // The cart API exposes single-step increase/decrease plus an explicit
  // set-quantity operation, so the cheapest matching call is used.
  const commit = (next) => {
    if (next === quantity) return;
    if (next < minimum) {
      onRemoveItem(item);
    } else if (maximum !== null && next > maximum) {
      setValue(quantity);
    } else if (next === quantity + 1) {
      onIncreaseItem(item);
    } else if (next === quantity - 1) {
      onDecreaseItem(item);
    } else {
      onSetItemQuantity(item, next);
    }
  };

  const scheduleCommit = (next) => {
    setValue(next);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => commit(next), QUANTITY_DEBOUNCE_MS);
  };

  const handleChange = (e) => {
    const raw = e.target.value;
    // An empty field is a transient typing state, never a quantity change.
    if (raw === "") {
      setValue("");
      return;
    }
    // `maxLength` isn't honoured on type="number" inputs by every browser,
    // so the 4-digit cap (matching the field's width) is enforced here too.
    if (raw.length > 4) return;
    const next = parseInt(raw, 10);
    if (isNaN(next)) return;
    scheduleCommit(next);
  };

  const handleBlur = () => {
    if (value === "") setValue(quantity);
  };

  const currentValue = value === "" ? quantity : value;
  const canIncrease = maximum === null || currentValue < maximum;

  return (
    <div className="cart-quantity-input">
      <CustomButton link small tight onClick={() => onRemoveItem(item)}>
        <Trash />
      </CustomButton>
      {currentValue > minimum ? (
        <CustomButton
          link
          small
          tight
          onClick={() => scheduleCommit(currentValue - 1)}
        >
          <RemoveMinus />
        </CustomButton>
      ) : null}
      <input
        type="number"
        inputMode="numeric"
        className="cart-quantity-input__field"
        min={minimum}
        maxLength={4}
        value={value}
        onChange={handleChange}
        onBlur={handleBlur}
      />
      {canIncrease ? (
        <CustomButton
          link
          small
          tight
          onClick={() => scheduleCommit(currentValue + 1)}
        >
          <AddPlus />
        </CustomButton>
      ) : null}
    </div>
  );
};

export default CartItemsQuantityInput;
